const WINDOW_MS = 200

export const DEFAULT_SILENCE_THRESHOLD_RATIO = 0.35
export const DEFAULT_MIN_SILENCE_DURATION_MS = 500
const REFERENCE_PERCENTILE = 75
const NOISE_FLOOR_PERCENTILE = 20 // typical ambient/background level (crowd, room tone, wind) when nobody is speaking
const ABSOLUTE_FLOOR_RMS = 0.006 // still ignore near-total digital silence/clipping as a sane minimum

const percentile = (sortedValues, p) => {
    if (!sortedValues.length) return 0
    const idx = Math.min(sortedValues.length - 1, Math.floor((p / 100) * sortedValues.length))
    return sortedValues[idx]
}

// Decodes the clip's source audio once and computes the per-200ms-window loudness (RMS) profile,
// plus a reference "typical active" level (see extractSilenceRanges) - the expensive part
// (fetch + decode + per-sample scan). Re-tuning sensitivity/minimum duration afterwards only needs
// extractSilenceRanges, a cheap pass over this already-computed profile, so a slider can re-run
// detection live without re-decoding audio on every change.
export const analyzeAudioLoudness = async (url) => {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext
    const ctx = new AudioContextClass()

    try {
        const arrayBuffer = await fetch(url).then(r => r.arrayBuffer())
        const audioBuffer = await ctx.decodeAudioData(arrayBuffer)

        const channelData = audioBuffer.getChannelData(0)
        const sampleRate = audioBuffer.sampleRate
        const windowSize = Math.floor((WINDOW_MS / 1000) * sampleRate)

        const windows = []
        for (let i = 0; i < channelData.length; i += windowSize) {
            let sumSquares = 0
            const end = Math.min(i + windowSize, channelData.length)
            for (let j = i; j < end; j++) sumSquares += channelData[j] * channelData[j]
            windows.push({ startMs: (i / sampleRate) * 1000, endMs: (end / sampleRate) * 1000, rms: Math.sqrt(sumSquares / (end - i)) })
        }

        const sortedRms = windows.map(w => w.rms).sort((a, b) => a - b)
        const referenceLevel = percentile(sortedRms, REFERENCE_PERCENTILE)
        const noiseFloorLevel = percentile(sortedRms, NOISE_FLOOR_PERCENTILE)

        return { windows, referenceLevel, noiseFloorLevel }
    } catch (e) {
        return { windows: [], referenceLevel: 0, noiseFloorLevel: 0 } // no decodable audio track - nothing to detect
    } finally {
        ctx.close()
    }
}

// How much quieter than the clip's own "typical active" loudness (referenceLevel) counts as
// silence. A fixed absolute RMS threshold only ever caught near-total digital silence - real
// recordings (wind, room tone, phone mic noise floor, outdoor ambiance) rarely drop that low even
// during an actual pause, so on typical footage it simply never fired anything. Scaling to each
// clip's own dynamic range makes "silence" mean "quiet relative to this specific clip" instead of
// "near-total digital silence", which is what actually corresponds to a pause a viewer would notice.
//
// The threshold is placed between the clip's own ambient noise floor (noiseFloorLevel) and its
// "typical active" level (referenceLevel), rather than as a flat fraction of referenceLevel alone.
// On footage with a loud, constant background (crowd noise, wind, room tone), referenceLevel and
// noiseFloorLevel both rise together - a flat fraction of referenceLevel then either under- or
// over-shoots depending on how loud that background already is. Interpolating between the two
// keeps thresholdRatio meaning the same thing (how far up the clip's own quiet-to-loud range the
// cutoff sits) regardless of how noisy the recording environment is.
export const extractSilenceRanges = ({ windows, referenceLevel, noiseFloorLevel = 0 }, {
    thresholdRatio = DEFAULT_SILENCE_THRESHOLD_RATIO,
    minSilenceDurationMs = DEFAULT_MIN_SILENCE_DURATION_MS
} = {}) => {
    const thresholdRms = Math.max(ABSOLUTE_FLOOR_RMS, noiseFloorLevel + (referenceLevel - noiseFloorLevel) * thresholdRatio)

    const ranges = []
    let current = null
    windows.forEach((w) => {
        if (w.rms < thresholdRms) {
            if (!current) current = { startMs: w.startMs, endMs: w.endMs }
            else current.endMs = w.endMs
        } else if (current) {
            ranges.push(current)
            current = null
        }
    })
    if (current) ranges.push(current)

    return ranges
        .filter(r => r.endMs - r.startMs >= minSilenceDurationMs)
        .map(r => ({ startMs: Math.round(r.startMs), endMs: Math.round(r.endMs), confidence: 0.8 }))
}
